import filters from "./filters/filters";
import LazyLoadingComponent from "../../../common/ts/components/LazyLoadingComponent";
import {connect} from "../../../common/ts/connect";
import pagination from "./pagination/pagination";
import location from "../../../common/ts/core/location";
import dom from "../../../common/ts/core/dom";
import tracking from "../../../common/ts/core/tracking";
import sorting from "./sorting/sorting";
import breadcrumb from "./breadcrumb/breadcrumb";
import emailRetention from "../components/retention/email/main";
import cmpAdsManager from "./cmpAdsManager/cmpAdsManager";
import http from "../../../common/ts/core/http";
import localStorage from "../../../common/ts/core/localStorage";
import webpush from "./webpush/webpush";
import googleOneTap from "./googleOneTap/googleOneTap";
import dfpRightBlock from "../components/dfpRightBlock/dfpRightBlock";
import {LISTINGS_SELECTOR, USER_SEARCH_SELECTOR} from "../selectors";
import header from "../components/header/header";
import {EventBus} from "../../../common/ts/events/EventBus";
import eventHandlers from "./eventHandlers";
import userSearchEventHandlers from "../components/userSearch/eventHandlers";
import {SerpClickSimilarAdsRequestedEvent, SerpClickSimilarAdsRequestedPayload, SerpLoadedEvent} from "./events";
import {isDesktopDevice, isMobileDevice} from "../device";
import autocomplete from "./filters/autocomplete";
import propertyTypeDropdown from "../components/propertyTypeDropdown";
import {
    FILTER_SEARCH_ID, GEO_SEARCH_ID
} from "./filters/constants";
import chips from "./chips";
import homeSearchBoxForm from "../components/homeSearchBoxForm";
import {FindIntent, Snippet} from "../components/snippet/snippet";
import SaveSearchModal from "../../../common/ts/components/modals/retention/SaveSearchModal";
import {MarketStatsStyles} from "../../../common/ts/components/charts/MarketStatsChart";
import {MarketStatsChartVersion, SerpMarketStatsChart} from "../../../common/ts/components/charts/SerpMarketStatsChart";
import {EMAIL_RETENTION_VISITED_LOCATIONS_LOCAL_STORAGE_KEY} from "../components/retention/email/main/constants";

const FIRST_RIGHT_DFP_SELECTOR = '.js-dfp-right-block__top';
const eventBus = EventBus.getInstance();

export interface MitulaSerpPageData {
    device: string;
    search: string;
    operationType: string;
    propertyType: string;
    filters: string;
    geoEntity: string;
    pageviewId: string;
    pageNumber: number;
    isPaid: boolean;
    formId: string;
    freeText: string;
    geoName: string;
    geoSlug: string;
    secondLevelGeoId: string;
    findIntent: FindIntent;
    isGDPRCountry: boolean;
    shouldShowProjectSnippet: boolean;
    shouldRequestWebPushPermission: boolean;
    shouldShowGoogleOneTapOnLoad: boolean;
    shouldShowEmailRetentionModal: boolean;
}

(window as any).initPage = (pageData: MitulaSerpPageData) => {
    connect(() => {
        eventHandlers.init();
        dom.init();
        location.init();
        tracking.init();
        http.init();
        localStorage.init();
        cmpAdsManager.init(eventBus, pageData.shouldShowGoogleOneTapOnLoad);
        header.init();
        sorting.init();
        new LazyLoadingComponent().setupLazyLoading();
        filters.init(pageData);
        userSearchEventHandlers.init();
        breadcrumb.init();
        pagination.init();
        webpush.init();
        googleOneTap.init(pageData.device);
        chips.init(pageData);
        propertyTypeDropdown.init();
        homeSearchBoxForm.init(pageData.pageviewId, true);
        dfpRightBlock.init({
            topBoundaryElement: dom.select(USER_SEARCH_SELECTOR),
            topSiblingElement: dom.select(FIRST_RIGHT_DFP_SELECTOR),
            bottomBoundaryElement: dom.select(LISTINGS_SELECTOR),
        });
        emailRetention.init({
            search: pageData.search,
            pageNumber: pageData.pageNumber,
            isPaid: pageData.isPaid,
            toggleTrackingName: isMobileDevice(pageData.device) ? 'email-retention-bottom-receive-news' : 'email-retention-modal-receive-news',
            submitTrackingName: isMobileDevice(pageData.device) ? 'email-retention-bottom-submit' : 'email-retention-modal-submit',
            isDesktop: isDesktopDevice(pageData.device),
        });
        let saveSearchModalFirstStep = new SaveSearchModal(pageData.search, pageData.isPaid, !isMobileDevice(pageData.device), "save-actual-search-form");
        saveSearchModalFirstStep.init();

        let emailRetentionModal = new SaveSearchModal(pageData.search, pageData.isPaid, !isMobileDevice(pageData.device), "save-email-retention-form", "#email-retention-modal", false);
        emailRetentionModal.init();

        [FILTER_SEARCH_ID, GEO_SEARCH_ID].forEach((id) => {
            autocomplete.init({
                id: id,
                geoSlug: pageData.geoSlug,
            });
        });
        initMarketStats(isMobileDevice(pageData.device));
        new Snippet({
            pageViewId: pageData.pageviewId,
            search: pageData.search,
            getSimilarsEvent: (params) => {
                const paramsWithIntent = {...params, findIntent: pageData.findIntent};
                eventBus.emit(
                    new SerpClickSimilarAdsRequestedEvent(paramsWithIntent as SerpClickSimilarAdsRequestedPayload)
                );
            },
            eventBus: eventBus,
            findIntent: pageData.findIntent,
        }).init();

        eventBus.emit(new SerpLoadedEvent({
            search: pageData.search,
            shouldShowEmailRetentionModal: pageData.shouldShowEmailRetentionModal || false,
            saveSearchModal:emailRetentionModal,
            shouldShowProjectSnippet: pageData.shouldShowProjectSnippet || false,
            shouldRequestWebPushPermission: pageData.shouldRequestWebPushPermission || false,
            shouldShowGoogleOneTapOnLoad: pageData.shouldShowGoogleOneTapOnLoad || false,
            storedSearches: localStorage.getItem(EMAIL_RETENTION_VISITED_LOCATIONS_LOCAL_STORAGE_KEY),
            operationType: pageData.operationType,
            filters: pageData.filters,
            geoEntity: pageData.geoEntity,
            pageviewId: pageData.pageviewId,
            serpUrl: window.location.href,
            device: pageData.device,
            isGDPRCountry: pageData.isGDPRCountry,
        }))
    });
    function initMarketStats(isMobile: boolean) {
        const marketStatsStyles: MarketStatsStyles = {
            lineColor: '#552D73',
            fillColor: '#FF50500D',
            pointColor: '#FFFFFF',
            pointHoverColor: '#552D73',
            axisBorderColor: '#E0E0E0',
            scalesLabelColor: isMobile ? '#4A4A4A' : '#7F7F7F',
            pointRadius: isMobile ? 5 : 8,
            tickPadding: isMobile ? 0 : 10,
            tooltipTitleFontSize: isMobile ? 10 : 14,
            tooltipBodyFontSize: isMobile ? 12 : 16,
            chartType: 'line',
            xAxisRotation: isMobile ? 90 : false,
            yAxisRotation : false,
            barThickness: undefined,
            barBorderRadius: undefined,
        };

        let chartCanvas = document.querySelector('.js-market-stats-chart') as HTMLElement;
        if (chartCanvas === null) {
            return;
        }
        const stats = chartCanvas.dataset.chartStats;
        if (stats === undefined) {
            return;
        }
        const config = chartCanvas.dataset.chartConfig;
        if (config === undefined) {
            return;
        }
        new SerpMarketStatsChart().init(marketStatsStyles, '.js-market-stats-chart', MarketStatsChartVersion.NO_POINTS);
    }
}